import type { UIViewInjectedProps } from '@uirouter/react';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';

import { api } from '@/api';
import { DrawerForm } from '@/components/drawer-form';
import { getAllowedFormFields } from '@/components/generic-form/utils';
import { PERMISSION_OBJ_COOPERATIVE } from '@/constants/permissions';
import { cooperativeStore } from '@/stores';
import { getTranslatedString } from '@/utils';
import { withoutRepeatValue } from '@/utils/controller';

import { getFormFields } from './setup';

const store = cooperativeStore;

export const CooperativeForm = observer(({ transition }: UIViewInjectedProps) => {
  const isCreate = transition.router.globals.current.name === 'base-layout.cooperatives.create';
  const { cooperativeId } = transition.router.globals.params;
  const foundItem = store.items.find((item: cyrian.api.cooperatives.Cooperative) => item.id === +cooperativeId);

  useEffect(() => {
    if (!isCreate && cooperativeId && !foundItem) {
      api.cooperatives
        .get(+cooperativeId)
        .source.then(({ data }) => store.addItem(data))
        .catch(() => {});
    }
  }, [isCreate, cooperativeId, foundItem]);

  const onClose = () => {
    transition.router.stateService.go('base-layout.cooperatives');
  };

  const initValues =
    !isCreate && foundItem
      ? {
          name: foundItem.name,
          code: foundItem.code,
          isEudrCompliant: foundItem.isEudrCompliant,
          consortiumIds: foundItem.consortiums?.map((c) => c.id) ?? [],
        }
      : {};

  const title = isCreate
    ? getTranslatedString('cooperatives.create-new')
    : getTranslatedString('cooperatives.cooperative-number', { 0: cooperativeId });

  const formFields = getFormFields();
  let allowedFields = getAllowedFormFields(formFields, isCreate, PERMISSION_OBJ_COOPERATIVE);
  allowedFields = allowedFields.map((field: any) => {
    if (field.name === 'consortiumIds' && foundItem?.consortiums) {
      return {
        ...field,
        params: {
          ...field.params,
          initialSelectedOptions: foundItem.consortiums.map((c) => ({
            value: c.id,
            label: c.name,
          })),
        },
      };
    }
    return field;
  });

  const resourceController = withoutRepeatValue(initValues, {
    create: (values: any) => {
      const payload: cyrian.api.cooperatives.CooperativeCreateParams = {
        name: values.name,
        code: values.code,
        isEudrCompliant: values.isEudrCompliant,
        consortiumIds: values.consortiumIds,
      };
      return api.cooperatives.create(payload).source.then(({ data }) => {
        store.addItem(data);
        return data;
      });
    },
    update: (values: any) => {
      const payload: cyrian.api.cooperatives.CooperativeUpdateParams = {
        name: values.name,
        code: values.code,
        isEudrCompliant: values.isEudrCompliant,
        consortiumIds: values.consortiumIds,
      };
      return api.cooperatives.update(foundItem.id, payload).source.then(({ data }) => {
        store.updateItem(data);
        return data;
      });
    },
  });

  return (
    <DrawerForm
      title={title}
      initValues={initValues}
      formFields={allowedFields}
      onClose={onClose}
      resourceId={!isCreate && foundItem ? foundItem.id : undefined}
      resourceController={resourceController}
      loaderCondition={!isCreate && !foundItem}
    />
  );
});
